/** Downhill modules: a carving skier body that sets the course's pace
 * (`body.carve`) and the timed course it races — gates threaded or missed,
 * a stopwatch with penalties, the finish (`rule.course`). */
import { bool, id, int, num, obj, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { approach, clamp, pushVelocity, speedMultiplier } from '../physics.js';
import { addTag, COMPOSE_TICK_HZ, overlaps, type EntityV1 } from '../world.js';

const rad = (deg: number) => (deg * Math.PI) / 180;

export type CarveConfig = {
  /** Speed along the skis, units/s: tucked (up or the primary button), plain, snowplow (down). */
  tuck: number;
  cruise: number;
  plow: number;
  /** Units/s² gaining speed, and shedding it (a snowplow bites harder). */
  accel: number;
  brake: number;
  /** Heading change while left/right is held, and the return to the fall
   * line when released (degrees/s); the widest heading either way. */
  turnRate: number;
  recenter: number;
  maxAngle: number;
  /** Fraction of the heading the drawable is rotated by (its attitude). */
  attitude: number;
  /** Clearance from each side of the view beyond the body. */
  band: { left: number; right: number };
  /** Scalar receiving the downhill pace (speed × cos heading), which the
   * scrolling course moves at. */
  paceScalar: string;
  /** Rough ground (moguls): entering one keeps `bumpSlow` of the speed and
   * shoves the body sideways at `bumpShove` units/s, easing over `bumpTicks`. */
  bumpTag: string;
  bumpSlow: number;
  bumpShove: number;
  bumpTicks: number;
  /** Publish its own position as the vitals respawn point (recover where it fell). */
  recoverInPlace: boolean;
};

type CarveCtx = ModuleCtx<CarveConfig>;

/** The input-selected speed target (along the skis), before the heading. */
function speedTarget(c: CarveConfig, tucked: boolean, plowing: boolean): number {
  return plowing ? c.plow : tucked ? c.tuck : c.cruise;
}

function publishPace(ctx: CarveCtx, pace: number): void {
  ctx.setScalar(ctx.config.paceScalar, pace);
}

/** `body.carve` — a skier on a slope that scrolls under it: the body stays
 * on its line across the view and carves. Left/right swing the heading at a
 * fixed rate (so the arc is tighter the slower it runs) up to `maxAngle`,
 * releasing returns it to the fall line; up (or the primary button) tucks
 * for the top speed, down snowplows to shed it. Across-slope motion is
 * speed × sin heading; the downhill pace (speed × cos heading) is published
 * for the course to scroll by, so traversing costs pace. Rough ground slows
 * and shoves it; a push (the push protocol: a crosswind) carries it across
 * on top of its carve (its across-slope `vx` only); a body knocked down (not
 * active) loses its speed and line. */
export const carve = defineModule<CarveConfig>({
  id: 'body.carve', version: 1, attach: 'entity', role: 'movement',
  summary: 'Carving skier: left/right swing the heading (tighter arcs when slow), up tucks, down snowplows; moves across the slope and publishes the downhill pace the course scrolls at; moguls slow and shove it.',
  config: obj({ tuck: num(0.5, 30), cruise: num(0.5, 30), plow: num(0, 30), accel: num(0.1, 60), brake: num(0.1, 120),
    turnRate: num(10, 720), recenter: num(0, 720), maxAngle: num(5, 85), attitude: num(0, 1),
    band: obj({ left: num(0, 32), right: num(0, 32) }), paceScalar: id(), bumpTag: id(), bumpSlow: num(0, 1), bumpShove: num(0, 20),
    bumpTicks: int(1, 120), recoverInPlace: bool() }),
  requires: [], events: ['carve', 'tuck', 'snowplow', 'bump'],
  presentation: { carve: { cue: 'dash' }, tuck: { cue: 'none' }, snowplow: { cue: 'land' },
    bump: { cue: 'bounce', vfx: { effect: 'burst', count: 4 } } },
  proofs: ['heading-steers-across', 'recentres-on-release', 'tuck-cruise-plow-speeds', 'tighter-arc-when-slow', 'publishes-downhill-pace',
    'traverse-costs-pace', 'held-in-band', 'mogul-slows-and-shoves', 'push-protocol', 'knocked-down-loses-speed', 'recovers-in-place'],
  animStates: () => ['idle', 'move', 'crouch'],
  check(c, context) {
    const errors: string[] = [];
    if (!(c.plow <= c.cruise && c.cruise <= c.tuck)) errors.push('speeds must order plow ≤ cruise ≤ tuck');
    if (c.band.left + c.band.right >= context.world.w) errors.push('the band leaves no room to ski');
    return errors;
  },
  init(ctx, e) {
    Object.assign(ctx.data(e), { heading: 0, speed: 0, turning: 0, mode: 'cruise', shove: 0, shoveTicks: 0, bumped: null });
    publishPace(ctx, 0);
  },
  systems: {
    move(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (e.status !== 'active') {
        // Down (a wipeout) or off the field: the run's speed and line are gone.
        Object.assign(d, { heading: 0, speed: 0, turning: 0, shove: 0, shoveTicks: 0 });
        e.vx = 0; e.vy = 0; e.angle = 0;
        publishPace(ctx, 0);
        return;
      }
      const input = e.slot !== null ? ctx.input(e.slot) : null;
      const dx = input?.dx ?? 0;
      const tucked = !!input && (input.dy < 0 || input.a), plowing = !!input && input.dy > 0 && !tucked;
      const mode = plowing ? 'snowplow' : tucked ? 'tuck' : 'cruise';
      if (mode !== d.mode && mode !== 'cruise') ctx.emit(mode, { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0 });
      if (dx !== 0 && dx !== read(d, 'turning')) ctx.emit('carve', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0, dir: dx });
      d.mode = mode; d.turning = dx;
      // Heading: degrees from the fall line, positive toward +x.
      let heading = read(d, 'heading');
      heading = dx !== 0 ? clamp(heading + dx * c.turnRate * ctx.dt, -c.maxAngle, c.maxAngle) : approach(heading, 0, c.recenter * ctx.dt);
      d.heading = heading;
      const t = rad(heading);
      const cap = c.tuck * speedMultiplier(e);
      const target = Math.min(cap, speedTarget(c, tucked, plowing));
      let speed = read(d, 'speed');
      speed = speed < target ? Math.min(target, speed + c.accel * ctx.dt) : Math.max(target, speed - (plowing ? c.brake : c.accel) * ctx.dt);
      d.speed = speed;
      // A mogul's shove eases out linearly over its ticks.
      let shove = 0;
      if (read(d, 'shoveTicks') > 0) {
        shove = read(d, 'shove') * (read(d, 'shoveTicks') / c.bumpTicks);
        d.shoveTicks = read(d, 'shoveTicks') - 1;
      }
      e.vx = speed * Math.sin(t) + shove; e.vy = 0;
      const minX = c.band.left + e.w / 2, maxX = ctx.world.bounds.w - c.band.right - e.w / 2;
      e.x = clamp(e.x + (e.vx + pushVelocity(ctx.signals('push'), e).vx) * ctx.dt, minX, maxX);
      e.angle = -t * c.attitude;
      publishPace(ctx, speed * Math.cos(t));
      if (c.recoverInPlace) ctx.signal('checkpoint', e, e, { x: e.x, y: e.y });
    },
    collide(ctx, e) {
      if (!e || e.status !== 'active') return;
      const c = ctx.config, d = ctx.data(e);
      const rough = ctx.entities({ tag: c.bumpTag, status: 'active' }).find(m => overlaps(ctx.world, e, m, 0.1));
      if (!rough) { d.bumped = null; return; }
      if (str(d, 'bumped') === rough.id) return;
      d.bumped = rough.id;
      d.speed = read(d, 'speed') * c.bumpSlow;
      d.shove = (e.x >= rough.x ? 1 : -1) * c.bumpShove; d.shoveTicks = c.bumpTicks;
      ctx.emit('bump', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0 });
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      ctx.setAnim(e, read(d, 'speed') < 0.2 ? 'idle' : d.mode === 'tuck' ? 'crouch' : 'move');
    },
  },
});

export type CourseConfig = {
  /** Gates (a pair of poles drawn as one kind): the racer threads one when
   * its centre lies inside the gate, `inset` in from each pole, as the gate
   * crosses its line. */
  gateTag: string;
  racerTag: string;
  inset: number;
  /** Points for each gate threaded (scored as it is passed). */
  gatePoints: number;
  /** Ticks added to the race time per missed gate (0: gates only mark the line). */
  penaltyTicks: number;
  /** Missing this many gates disqualifies the racer: the run is lost. */
  disqualifyAfter: number;
  /** Signal that the racer crossed the finish (a scroll track's `route-end`). */
  finishOn: string;
  /** Scalar in [0, 1] of the way down the course (`route.progress`). */
  progressScalar: string;
  /** Points per second under par at the finish (none at or over par). */
  parTicks: number;
  pointsPerSecond: number;
};

type CourseCtx = ModuleCtx<CourseConfig>;

/** Race time in ticks: the stopwatch plus every penalty. */
function raceTicks(ctx: CourseCtx): number {
  return read(ctx.state, 'elapsed') + read(ctx.state, 'missed') * ctx.config.penaltyTicks;
}

/** `m:ss.t` of a tick count. */
export function raceClock(ticks: number): string {
  const tenths = Math.floor((ticks * 10) / COMPOSE_TICK_HZ);
  const minutes = Math.floor(tenths / 600), seconds = Math.floor((tenths % 600) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${tenths % 10}`;
}

/** Which side of the racer's line a gate is on (its centre against the
 * racer's), so a crossing is judged whichever way the course flows. */
function side(gate: EntityV1, racer: EntityV1): number {
  return Math.sign(gate.y - gate.h / 2 - (racer.y - racer.h / 2));
}

function racerOf(ctx: CourseCtx): EntityV1 | undefined {
  return ctx.entities({ tag: ctx.config.racerTag, status: 'any' })[0];
}

/** `rule.course` — a timed downhill course for one racer. The stopwatch
 * runs from the drop-in to the finish; each gate is judged once, as it
 * crosses the racer's line — threaded (a chime and `gatePoints`) or missed (a penalty added
 * to the race time, the gate dimmed), and missing `disqualifyAfter` of them
 * loses the run (disqualified); at the finish signal the clock stops,
 * points for every second under par are scored and the goal is done.
 * Publishes `goal.progress` (the way down the course). */
export const course = defineModule<CourseConfig>({
  id: 'rule.course', version: 1, attach: 'global', role: 'rule',
  summary: 'Timed course: a stopwatch from the start, gates judged threaded or missed as they cross the racer (a miss adds a time penalty; too many disqualify), the finish stops the clock and scores time under par.',
  config: obj({ gateTag: id(), racerTag: id(), inset: num(0, 4), gatePoints: int(0, 10000), penaltyTicks: int(0, 900), disqualifyAfter: int(1, 100), finishOn: readsSignal(), progressScalar: id(),
    parTicks: int(30, 60 * 60 * COMPOSE_TICK_HZ), pointsPerSecond: int(0, 10000) }),
  requires: ['flow.objective'], events: ['gate', 'gate-miss', 'disqualified', 'finish'],
  presentation: { gate: { cue: 'success', vfx: { effect: 'flash', palette: 'slot' } }, 'gate-miss': { cue: 'fail', vfx: { effect: 'popup', text: 'MISSED' } },
    disqualified: { cue: 'lose', vfx: { effect: 'shake' } }, finish: { cue: 'success', vfx: { effect: 'burst', count: 12 } } },
  proofs: ['stopwatch-runs-until-finish', 'gate-threaded-inside-inset-scores', 'gate-missed-adds-penalty', 'judged-once-either-flow', 'too-many-misses-disqualify',
    'finish-scores-under-par',
    'goal-progress'],
  animStates: () => [],
  hud: () => [{ id: 'time', label: 'TIME', kind: 'text' }, { id: 'gates', label: 'GATES', kind: 'counter' }, { id: 'missed', label: 'MISSED', kind: 'counter' }],
  hudValues: ctx => ({ time: raceClock(raceTicks(ctx)), gates: read(ctx.state, 'passed'), missed: read(ctx.state, 'missed') }),
  initGlobal(ctx) {
    Object.assign(ctx.state, { elapsed: 0, passed: 0, missed: 0, finished: false, sides: {} });
    ctx.setScalar('goal.progress', 0);
  },
  systems: {
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      ctx.setScalar('goal.progress', clamp(ctx.scalar(c.progressScalar, 0), 0, 1));
      if (ctx.world.phase !== 'active' || s.finished === true) return;
      s.elapsed = read(s, 'elapsed') + 1;
      const racer = racerOf(ctx);
      const sides = s.sides as Record<string, number>;
      if (racer) {
        for (const gate of ctx.entities({ tag: c.gateTag, status: 'any' })) {
          if (gate.tags.includes('judged')) continue;
          const now = side(gate, racer), was = sides[gate.id];
          if (was === undefined || was === 0) { sides[gate.id] = now; continue; }
          if (now === was) continue;
          delete sides[gate.id];
          addTag(gate, 'judged');
          const at = { x: gate.x, y: gate.y - gate.h / 2 };
          if (Math.abs(racer.x - gate.x) <= gate.w / 2 - c.inset) {
            s.passed = read(s, 'passed') + 1;
            if (racer.slot !== null) ctx.addScore(racer.slot, c.gatePoints);
            ctx.emit('gate', { ...at, slot: racer.slot ?? 0, passed: read(s, 'passed') });
          } else {
            s.missed = read(s, 'missed') + 1;
            gate.alpha = 0.45;
            ctx.emit('gate-miss', { ...at, slot: racer.slot ?? 0, missed: read(s, 'missed') });
            if (read(s, 'missed') >= c.disqualifyAfter) {
              ctx.emit('disqualified', { missed: read(s, 'missed') });
              ctx.end('lost', 'disqualified');
              return;
            }
          }
        }
      }
      // Forget gates that scrolled away before they were judged.
      for (const key of Object.keys(sides)) if (!ctx.entity(key)) delete sides[key];
      if (!ctx.signals(c.finishOn).length) return;
      s.finished = true;
      const ticks = raceTicks(ctx);
      const points = Math.floor((Math.max(0, c.parTicks - ticks) / COMPOSE_TICK_HZ) * c.pointsPerSecond);
      for (let slot = 0; slot < ctx.world.playerCount; slot++) ctx.addScore(slot, points);
      ctx.emit('finish', { ...(racer ? { x: racer.x, y: racer.y - racer.h / 2 } : {}), time: raceClock(ticks), points });
      ctx.setScalar('goal.done', 1);
    },
  },
  // The next gate still to come pulls the racer toward its middle.
  solverTargets(ctx, _e, player) {
    const sides = ctx.state.sides as Record<string, number>;
    let best: { gate: EntityV1; gap: number } | null = null;
    for (const gate of ctx.entities({ tag: ctx.config.gateTag, status: 'active' })) {
      if (gate.tags.includes('judged')) continue;
      const gap = Math.abs(gate.y - gate.h / 2 - (player.y - player.h / 2));
      if (sides[gate.id] !== undefined && sides[gate.id] !== side(gate, player)) continue;
      if (!best || gap < best.gap) best = { gate, gap };
    }
    return best ? [{ x: best.gate.x, y: best.gate.y - best.gate.h / 2, weight: 2, label: 'gate' }] : [];
  },
  // Each gate threaded is finished work worth more than its pull.
  solverProgress: ctx => read(ctx.state, 'passed') * 40,
});
